<script lang="ts">
  import { ChevronRight, Ellipsis } from "../icons/index.js";
  import JsonNode from "./JsonNode.svelte";

  interface Props {
    data: unknown;
    defaultOpen?: boolean;
    depth?: number;
    keyName?: string | number;
  }

  const { data, defaultOpen = true, depth = 0, keyName }: Props = $props();

  // svelte-ignore state_referenced_locally
  let open = $state(defaultOpen);

  const isObject = (v: unknown): v is Record<string, unknown> =>
    v !== null && typeof v === "object" && !Array.isArray(v);
  const isExpandable = (v: unknown) => isObject(v) || Array.isArray(v);

  const entries = $derived(
    Array.isArray(data)
      ? data.map((v, i) => [i, v] as const)
      : isObject(data)
        ? Object.entries(data)
        : [],
  );

  const bracket = $derived(Array.isArray(data) ? ["[", "]"] : ["{", "}"]);
  const preview = $derived.by(() => {
    if (Array.isArray(data)) {
      const len = data.length;
      return `${len} ${len === 1 ? "item" : "items"}`;
    }
    const len = Object.keys(data as object).length;
    return `${len} ${len === 1 ? "key" : "keys"}`;
  });

  function color(v: unknown): string {
    switch (typeof v) {
      case "string":
        return "text-pri-800";
      case "number":
        return "text-sec-700";
      case "boolean":
        return "text-sec-600";
      default:
        return "text-mono-600";
    }
  }
</script>

{#if depth > 20}
  <span class="text-mono-600"><Ellipsis size={12} /></span>
{:else if isExpandable(data)}
  <div class="font-mono text-xs">
    <button
      class="group flex items-center rounded-md pr-1 transition-all duration-200 ease-out hover:bg-alpha-025"
      onclick={() => (open = !open)}
      type="button"
    >
      <span
        class:rotate-90={open}
        class="flex h-4 w-4 shrink-0 items-center justify-center text-mono-600 transition-all duration-200 ease-out group-hover:text-mono-900"
      >
        <ChevronRight size={12} />
      </span>
      {#if keyName !== undefined}<span class="text-mono-700 group-hover:text-mono-900"
          >{keyName}:&nbsp;</span
        >{/if}
      <span class="text-mono-600 transition-colors duration-200 ease-out group-hover:text-mono-900"
        >{bracket[0]}</span
      >
      {#if !open}<span
          class="text-mono-800 transition-colors duration-200 ease-out group-hover:text-mono-900"
          >{preview}</span
        ><span
          class="text-mono-600 transition-colors duration-200 ease-out group-hover:text-mono-900"
          >{bracket[1]}</span
        >{/if}
    </button>
    {#if open}
      <div class="ml-1.5 border-l border-alpha-100 pl-3">
        {#each entries as [key, value], i}
          {#if isExpandable(value)}
            <div class="flex">
              <JsonNode data={value} defaultOpen={depth < 1} depth={depth + 1} keyName={key} />
              {#if i < entries.length - 1}<span class="text-mono-500">,</span>{/if}
            </div>
          {:else}
            <div class="flex">
              <span class="shrink-0 text-mono-700">{key}:&nbsp;</span>
              <span class={[color(value), typeof value === "string" && "whitespace-pre-wrap"]}
                >{String(value)}</span
              >
              {#if i < entries.length - 1}<span class="text-mono-500">,</span>{/if}
            </div>
          {/if}
        {/each}
      </div>
      <span class="ml-1.5 text-mono-600">{bracket[1]}</span>
    {/if}
  </div>
{:else}
  <span
    class={["font-mono text-xs", color(data), typeof data === "string" && "whitespace-pre-wrap"]}
    >{String(data)}</span
  >
{/if}
